import React, { useCallback, useState, type ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Calendar, Close } from '@mint-lib/icons';
import { useFlags } from '@mint-lib/routing-context';
import { cn } from '@mint-lib/tw-utils';

import { useMobile } from '../../hooks/useMobile.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '../../index.js';
import { Button } from '../../lib/button.jsx';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../../lib/input-group.jsx';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '../../lib/popover.jsx';

type InputTriggerProps = Readonly<{
  /** INPUT PROPS */
  /**
   * Class name for the datepicker input
   */
  className?: string;
  /**
   * The size of the datepicker input
   */
  size?: 'medium' | 'small';
  /**
   * If true, the datepicker input will be clearable
   */
  clearable?: boolean;
  /**
   * The variant of the datepicker input
   */
  variant?: 'single' | 'range';
  /**
   * The color of the datepicker input
   */
  color?: 'default' | 'warning' | 'error';
  /**
   * The readOnly state of the datepicker input
   */
  readOnly?: boolean;
  /**
   * The disabled state of the datepicker input
   */
  disabled?: boolean;
  /**
   * Set `true` if you need the input to inherit its parent width
   */
  fullWidth?: boolean;
  /**
   * The value of the datepicker input
   */
  value?: string;
  /**
   * The onChange event handler for the datepicker input
   */
  onChangeInput?: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * The ref for the datepicker input container, which is used to position the popover
   */
  ref?: React.Ref<HTMLDivElement>;
}>;

type CalendarInputProps = Readonly<{
  isMobile?: boolean;

  /**
   * The alignment of the datepicker popover
   */
  align?: 'start' | 'center' | 'end';
  /**
   * The class names for the datepicker popover
   */
  popoverClassName?: string;

  /**
   * The class names for the datepicker dialog (mobile)
   */
  dialogClassName?: string;
  /**
   * The onClose event handler for the datepicker dialog (mobile)
   */
  onClose?: () => void;
  /**
   * The onApply event handler for the datepicker dialog (mobile)
   */
  onApply?: () => void;
  /**
   * onReset event handler for the datepicker dialog (mobile)
   */
  onReset?: () => void;

  /**
   * If true, the container for the portal will be in the document.body
   */
  isPortal?: boolean;
  /**
   * The trigger element for the datepicker popover or dialog, which is typically the datepicker input
   */
  trigger?: React.ReactNode;
  /**
   * The children to be rendered inside
   */
  children?: React.ReactNode;
  /**
   * If true, the datepicker popover will be open
   */
  open?: boolean;
  /**
   * The onOpenChange event handler for the datepicker popover
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * The portal container element for the popover
   */
  container?: React.ComponentProps<typeof PopoverPortal>['container'];
}>;

function CalendarInput({
  className,
  children,
  color = 'default',
  align = 'start',
  clearable = true,
  variant = 'single',
  fullWidth,
  value,
  onChangeInput,
  size = 'medium',
  open,
  onOpenChange,
  isPortal,
  container,
  popoverClassName,
  readOnly,
  disabled,
  onApply,
  onReset,
  onClose,
}: CalendarInputProps & InputTriggerProps) {
  const ff = useFlags('@mint/arm', ['datePickerMobile']);
  const isMobile = useMobile();
  const { t } = useTranslation('@mint-lib/tw-components');
  const [internalContainer, setInternalContainer] =
    useState<HTMLDivElement | null>(null);
  const [internalValue, setInternalValue] = useState<string>(value || '');
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    setInternalContainer((prev) => (prev === node ? prev : node));
  }, []);
  const effectiveContainer = container ?? internalContainer;
  const portalContainer = isPortal ? document.body : effectiveContainer;

  const widthClassName = fullWidth ? 'w-full' : 'w-[283px]';

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (onChangeInput) {
      onChangeInput({
        target: { value: '' },
      } as ChangeEvent<HTMLInputElement>);
    }
  };
  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    onChangeInput?.(e);
  };

  return ff.datePickerMobile.enabled ? (
    <DatepickerVariantComponent
      open={open}
      onOpenChange={onOpenChange}
      align={align}
      popoverClassName={popoverClassName}
      container={portalContainer}
      trigger={
        <InputTrigger
          size={size}
          color={color}
          className={cn(className)}
          fullWidth={fullWidth}
          clearable={clearable}
          variant={variant}
          value={value}
          onChangeInput={onChangeInput}
          readOnly={readOnly}
          disabled={disabled}
          ref={setContainerRef}
        />
      }
      isMobile={isMobile}
      onApply={onApply}
      onReset={onReset}
      onClose={onClose}
    >
      {children}
    </DatepickerVariantComponent>
  ) : (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <InputGroup
          ref={setContainerRef}
          size={size}
          color={color}
          className={cn(
            'cursor-pointer caret-transparent-main',
            widthClassName,
            className,
          )}
        >
          <InputGroupInput
            type={'text'}
            className="cursor-pointer"
            data-testid="datepicker-input"
            value={value ?? internalValue}
            placeholder={
              variant === 'single' ? t('Select date') : t('Select date range')
            }
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleChangeInput}
          />

          <InputGroupAddon align="inline-end" aria-label="clear-button">
            {value && clearable && (
              <Button icon size="small" variant="text" onClick={handleClear}>
                <Close />
              </Button>
            )}
          </InputGroupAddon>
          <InputGroupAddon align="inline-end">
            <Calendar />
          </InputGroupAddon>
        </InputGroup>
      </PopoverTrigger>
      {portalContainer && (
        <PopoverPortal container={portalContainer}>
          <PopoverContent
            className={cn(popoverClassName)}
            align={align}
            data-testid="datepicker-input-popover"
          >
            {children}
          </PopoverContent>
        </PopoverPortal>
      )}
    </Popover>
  );
}

function InputTrigger(props: InputTriggerProps) {
  const { t } = useTranslation('@mint-lib/tw-components');
  const [internalValue, setInternalValue] = useState<string>(props.value || '');
  const widthClassName = props.fullWidth ? 'w-full' : 'w-[283px]';
  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    props.onChangeInput?.(e);
  };
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (props.onChangeInput) {
      props.onChangeInput({
        target: { value: '' },
      } as ChangeEvent<HTMLInputElement>);
    }
  };
  return (
    <InputGroup
      size={props.size}
      color={props.color}
      className={cn('cursor-pointer', widthClassName, props.className)}
      {...props}
    >
      <InputGroupInput
        type={'text'}
        className="cursor-pointer caret-transparent-main"
        data-testid="datepicker-input"
        value={props.value ?? internalValue}
        placeholder={
          props.variant === 'single' ? t('Select date') : t('Select date range')
        }
        disabled={props.disabled}
        readOnly={props.readOnly}
        onChange={handleChangeInput}
      />

      <InputGroupAddon align="inline-end" aria-label="clear-button">
        {props.value && props.clearable && (
          <Button icon size="small" variant="text" onClick={handleClear}>
            <Close />
          </Button>
        )}
      </InputGroupAddon>
      <InputGroupAddon align="inline-end">
        <Calendar />
      </InputGroupAddon>
    </InputGroup>
  );
}

function DatepickerPopover(props: CalendarInputProps) {
  return (
    <Popover open={props.open} onOpenChange={props.onOpenChange}>
      <PopoverTrigger asChild>{props.trigger}</PopoverTrigger>
      <PopoverPortal container={props.container}>
        <PopoverContent
          className={cn(props.popoverClassName)}
          align={props.align}
          data-testid="datepicker-input-popover"
        >
          {props.children}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}
function DatepickerDialog(props: CalendarInputProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogTrigger asChild>{props.trigger}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          size="s"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={props.onClose}
        >
          <DialogHeader onClose={props.onClose}>
            <DialogTitle>Choose a date range</DialogTitle>
          </DialogHeader>
          <DialogContent className="justify-center">
            {props.children}
          </DialogContent>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
}

const DatepickerVariantComponent = (
  props: CalendarInputProps & InputTriggerProps,
) => {
  const Component = props.isMobile ? DatepickerDialog : DatepickerPopover;
  return <Component {...props} />;
};

export default CalendarInput;
